import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import storageClient from '../../storageClient';
import { delay } from "bluebird";
import SeparationList from "../SeparationList";
import SoundSeparationDialog from "components/Dialogs/SoundSeparationModal";
import { useLocation } from "react-router-dom";
import PlayerModal from "components/Dialogs/PlayerModal";
import { useDispatch, useSelector } from "react-redux";
import { addSeparation, removeSeparation } from "../../redux/slices/AudioFilesSlice";
import { SeparationStatus } from "const";
import { IsTaskRunning, GetSeparationKey, UpdatePendingSeparation, GetBlobUrlForFile } from "utils";
import { reduxStore } from "../../redux/store";

function FileSeparations() {
  const location = useLocation();
  const file = location.state?.file;
  const [playableStems, setPlayableStems] = useState([]);
  const [openSeparationDialog, setOpenSeparationDialog] = useState(false);
  const [openPlayerModal, setOpenPlayerModal] = useState(false);
  const dispatch = useDispatch();

  const separationList = useSelector( (state) => state.audioFiles[file?.key]?.separations || {} );

  // Delete a separation (including all stem files) in R2
  const handleDelete = async (separationKey) => {
    try {
      if (!separationKey || typeof separationKey !== "string") {
        console.warn("Invalid separationKey provided:", separationKey);
        return;
      }

      const listedObjects = await storageClient.list({ key: separationKey });

      if (!listedObjects?.length) {
        console.info("No files found in the specified folder.");
        return;
      }

      const keysToDelete = listedObjects.map(obj => obj.key);

      await storageClient.remove(keysToDelete);

      dispatch(
        removeSeparation({
          fullSeparationKey: separationKey,
          sourceKey: file.key,
        })
      );
    } catch (error) {
      console.error("Error deleting separation:", {
        message: error.message,
        stack: error.stack,
        response: error.response?.data || null,
      });
    }
  }

  const shouldLoadStems = async (stems) => {
      for (const stem of stems) {
        const keyFound = playableStems.some((url) =>
          decodeURIComponent(url).includes(stem.key)
        );

        if (!keyFound) {
          return true; // If any stem in this separation is not already loaded, load all of them.
        }
      }

    return false; // All of the stems are already loaded..  no need to reload.
  };

  // Load a new set of stems
  const loadStems = async (stems) => {
    if (await shouldLoadStems(stems)) {
      try {
        const playableStems = await prepareStemsForPlayback(stems);
        setPlayableStems(playableStems);
      } catch (error) {
        if (error.response.status === 403) {
          // show download/streaming quota exceeded dialog
          console.error("Forbidden: Quota exceeded:", error);
        } else {
          console.error("Error determining if a download is allowed:", error);
        }
      }
    }
  };

  // Load stems for this particular separation
  const handleClick = async (filteredStems) => {
    await loadStems(filteredStems);
    setOpenPlayerModal(true);
    return;
  };

  const handleSeparationCancel = () => {
    setOpenSeparationDialog(false);
  };

  const handlePlayerClose = () => {
    setOpenPlayerModal(false);
  };

  const handleSeparationClose = async (pendingSeparation) => {
    const separationKey = `${GetSeparationKey(file.metadata)}/${pendingSeparation.method}/${pendingSeparation.model}`;
    const separation = CreateReduxSeparationObject(
      separationKey,
      pendingSeparation.key,
      pendingSeparation.taskArn,
    );

    dispatch(
      addSeparation({
        newSeparation: {
          separationKey,
          separation,
        },
      })
    );
    setOpenSeparationDialog(false);

    // Correct call to pollTaskCompletion:
    await pollTaskCompletion(file.key, separationKey, 3 * 60 * 1000, dispatch);
  };
  
  // Creates a new, pending separation object to store in Redux.  Will be updated when the
  // ECS task completes.
  function CreateReduxSeparationObject(separationKey, sourceKey, taskArn) {
    const keyParts = separationKey.split("/");

    return {
      sourceKey: sourceKey,
      stems: null,
      method: keyParts[keyParts.length - 2],
      model: keyParts[keyParts.length - 1],
      status: SeparationStatus.PENDING,
      taskArn: taskArn,
      zipKey: null,
      size: null,
    };
  }

  // Progressive delay for polling
  const getDelayForAttempt = (attempt) => {
    if (attempt <= 20) return 30_000;   // 30 seconds
    if (attempt <= 30) return 60_000;   // 60 seconds
    return 90_000;                      // 90 seconds
  };

  const pollTaskCompletion = async (
    fileKey,
    separationKey,
    initialDelayInMilliseconds,
    dispatch
  ) => {
    // wait for initialDelayInMilliseconds to avoid unnecessary poll requests at the beginning of a separation request.
    await delay(initialDelayInMilliseconds);

    const maxAttempts = 50;
    let attempts = 0;

    while (attempts++ < maxAttempts) {
      const state = reduxStore.getState();
      const file = state.audioFiles[fileKey];
      const pendingSeparation = file?.separations?.[separationKey];

      if (!pendingSeparation) {
        console.warn(`Separation not found: ${separationKey}`);
        return;
      }

      try {
        const isRunning = await IsTaskRunning(pendingSeparation.taskArn);

        if (!isRunning) {
          await UpdatePendingSeparation(file, separationKey, dispatch);
          return;
        }
      } catch (err) {
        console.warn(`Polling error on attempt ${attempts}:`, err);
        // optionally decide whether to retry or break here
      }

      const delayTime = getDelayForAttempt(attempts);
      console.log(`Polling attempt ${attempts}, waiting ${delayTime / 1000}s...`);
      await delay(delayTime);
    }

    console.warn(
      `Polling stopped after ${maxAttempts} attempts. Task may still be running: ${separationKey}`
    );
  };

  const getStemNameFromKey = (key) => {
      if (!key) return null;
      const parts = key.split('/');
      const filename = parts[parts.length - 1]; // "bass.mp3"
      const [stem] = filename.split('.');       // ["bass", "mp3"]
      return stem;
    };
  
  const prepareStemsForPlayback = async (stems) => {
    try {
      const blobbedStems = await Promise.all(
        stems.map(async (stem) => { 
          const url = await GetBlobUrlForFile(stem.key);
          const title = getStemNameFromKey ( stem.key);
          return url ? { ...stem, title, url } : null;
        })
      );

      return blobbedStems.filter(item => item !== null);

      } catch (error) {
        console.error("Error getting presigned URLs for stems:", error);
        return [];
      }
  };

  return (
    <Box className="App-body">
      <Box className="App-listbox" width="100vw">
        <Box sx={{ height: "100%", width: { xs: "94%", sm: "80%" } }}>
          <Box
            sx={{
              height: Object.keys(separationList).length > 0 ? "60%" : "20%",
              flexGrow: 1,
              overflowY: "auto",
            }}
          >
            {Object.keys(separationList).length > 0 && (
              <SeparationList
                separationList={separationList}
                onDelete={handleDelete}
                onItemClick={handleClick}
              />
            )}
          </Box>

          <Box
            sx={{
              borderBottom: 1,
              display: "flex",
              borderColor: "divider",
              justifyContent: "center",
              alignItems: "center",
              height: "40%",
            }}
          >
            <Button
              onClick={() => {
                setOpenSeparationDialog(true);
              }}
              sx={{ color: "white.main", backgroundColor: "primary.main" }}
              autoFocus
              variant="contained"
            >
              <Typography color="white">Create New Separation</Typography>
            </Button>
          </Box>
        </Box>
      </Box>
      <PlayerModal
        open={openPlayerModal}
        onClose={handlePlayerClose}
        file={file}
        playableStems={playableStems}
      />
      <SoundSeparationDialog
        open={openSeparationDialog}
        onClose={handleSeparationClose}
        onCancel={handleSeparationCancel}
        file={file}
      />
    </Box>
  );
}

export default FileSeparations;
